// ─────────────────────────────────────────────────────────────
// EDIT YOUR PARTY DETAILS HERE
// ─────────────────────────────────────────────────────────────

export const party = {
  name: 'KANCHELLA',
  tagline: 'Palm Springs Birthday Celebration',
  hostLine: 'Dan and Cam Present:',
  year: '2026',
  dateLong: 'October 2–4',
  dateShort: '10 / 02–04',
  gates: 'A weekend in the desert',
  venueName: 'The Parker Palm Springs',
  venueAddress: '4200 E. Palm Canyon Drive, Palm Springs, CA 92264',
  venueMapUrl:
    'https://www.google.com/maps/search/?api=1&query=The+Parker+Palm+Springs+4200+E+Palm+Canyon+Drive+Palm+Springs+CA+92264',
  estateMapUrl: 'https://www.parkerpalmsprings.com/estate-map',
  accommodations: 'All accommodations are provided — just bring your festival fit.',
  dressCode: 'Festival chic — boho, fringe, denim, sequins & statement accessories.',
  rsvpDeadline: 'Please complete registration by August 28',
} as const;

export type ScheduleItem = {
  time: string;
  title: string;
  detail?: string;
  location: string;
  attire?: string;
};

export type FestivalDay = {
  id: string;
  label: string;
  eyebrow: string;
  items: ScheduleItem[];
  footnote?: string;
};

export const festivalDays: FestivalDay[] = [
  {
    id: 'friday',
    label: 'Friday',
    eyebrow: 'Friday • Day One',
    items: [
      {
        time: '4:00 PM',
        title: 'Festival Gates Open',
        detail: 'Guest check-in',
        location: 'Parker Palm Springs Front Desk',
      },
      {
        time: '5:00–6:00 PM',
        title: 'Golden Hour',
        detail: 'Welcome cocktails',
        location: 'Counter Reformation',
        attire: 'California Casual',
      },
      {
        time: '6:00–7:30 PM',
        title: 'Backstage',
        detail: 'Freshen up before the main event.',
        location: 'Guest Rooms',
      },
      {
        time: '7:30–9:30 PM',
        title: 'Opening',
        detail: 'Welcome dinner',
        location: 'Palm Court',
        attire: 'Festival Chic',
      },
    ],
  },
  {
    id: 'saturday',
    label: 'Saturday',
    eyebrow: 'Saturday • Day Two',
    items: [],
    footnote: 'To be released',
  },
  {
    id: 'sunday',
    label: 'Sunday',
    eyebrow: 'Sunday • Day Three',
    items: [
      {
        time: '11:00 AM',
        title: 'Festival Grounds Close',
        detail: 'Hotel check-out',
        location: 'Parker Palm Springs Front Desk',
      },
    ],
  },
];

export const packingList: string[] = [
  'Festival Chic looks (boho, fringe, denim, sequins & statement accessories)',
  'Swimwear for Day Club',
  'Comfortable shoes for dancing late into the night',
  'Sunglasses & SPF for the Palm Springs sun',
  'A light layer for the late-night after hours',
  'Portable phone charger',
  'An appetite for good food and good music',
];

export type TravelNote = {
  title: string;
  detail: string;
};

export const travelNotes: TravelNote[] = [
  {
    title: 'Closest Airport',
    detail:
      "Palm Springs International Airport (PSP) is just a 10-minute drive from the hotel. If you're flying into another airport, we recommend allowing extra travel time.",
  },
  {
    title: 'Hotel Check-In',
    detail: 'Friday beginning at 4:00 PM',
  },
  {
    title: 'Hotel Check-Out',
    detail: 'Sunday by 11:00 AM',
  },
  {
    title: 'Getting Here',
    detail:
      "Guests are responsible for arranging transportation to and from Palm Springs. Once you've arrived, we'll take care of the rest.",
  },
  {
    title: 'Getting Around',
    detail: 'Uber and Lyft are readily available throughout the Palm Springs area.',
  },
];

export type Bring = {
  emoji: string;
  label: string;
  note: string;
};

export const essentials: Bring[] = [
  { emoji: '🕶️', label: 'Festival Fit', note: 'Dress the part' },
  { emoji: '💃', label: 'Dancing Shoes', note: 'The floor stays open' },
  { emoji: '📸', label: 'Good Vibes', note: 'And your camera' },
  { emoji: '🎁', label: 'Yourself', note: 'Presence over presents' },
];

export const vibeImage = {
  src: 'https://cdn.magicpatterns.com/uploads/v7qWv7ewonhndXtALdi4Gj/3e1fc5b3-829e-44a9-b300-2f1b58ad8bde.png',
  alt: 'Kanchella vibe: a Palm Springs pool at sunset, a neon Kanchella dance floor, and a fire pit lounge at night',
};

export const gallery = [
  {
    src: 'https://cdn.magicpatterns.com/uploads/uu8Q96naNEN168zaZPNC1d/Grounds_From_Roof_O.jpg',
    alt: 'The Parker Palm Springs grounds at dusk, lit pathways winding through gardens',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/4BpBsJQFTPz73rswVEXssX/Overhead-shot-of-Silicone-Valley-pool-and-grounds_14931299-1453-4e8e-8edf-301aa15823ff.jpg',
    alt: 'Overhead view of the Parker pool with striped umbrellas, palms and mountains beyond',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/iZHS4LHyCXMcbTHq4Luwry/ec5ca06f450ca00c8d8f054c170c2afa.jpg',
    alt: 'Festival crowd dancing among palm trees at golden hour',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/k8zhNPWw4GjDG6SBe4oYyr/parker-palm-springs.jpg',
    alt: 'Bougainvillea-covered walls and black and white striped umbrellas on the Parker lawn',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/hacuwU5rvnTZuVziYZMP71/d51192c24ed62d7a95004c56139a8292.jpg',
    alt: 'Friends in festival outfits walking the grounds together',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/s7pHkTbSFSnnGf5ndiCV8p/ChatGPT_Image_Jul_30,_2026,_06_34_01_PM.png',
    alt: 'Palm Springs pool party with disco balls, floats and champagne',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/h3dPi2v2SzFJxdjjyAzEGE/ChatGPT_Image_Jul_31,_2026,_01_43_26_PM.png',
    alt: 'Neon-lit festival tower glowing beside a ferris wheel at night',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/kH4sYh2VqcbZX5yMNKMpCx/ChatGPT_Image_Jul_30,_2026,_06_42_45_PM.png',
    alt: 'Couple in boho festival attire walking through the desert at sunset',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/7RL7Psc5FXXzwVNBvaQpdf/4d8e7703cd430212b0b4b85e29b43138.jpg',
    alt: 'Guests relaxing under palm trees and orange shade sails',
  },
  {
    src: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSc4eVTLN8AU0D63HbMvYUF_uRGN_qvai7bUvDl9pNOtQ&s=10',
    alt: 'The Parker Palm Springs',
  },
  {
    src: 'https://www.desertsun.com/gcdn/-mm-/767dd4fa527025c04777e70ef356774bdb65cba7/c=0-68-667-958/local/-/media/PalmSprings/None/2014/09/30/1412115030003-webParker-004.JPG?width=458&height=610&fit=crop&format=pjpg&auto=webp',
    alt: 'A lounge area at The Parker Palm Springs',
  },
];

export const HERO_IMAGE =
  'https://cdn.magicpatterns.com/uploads/aHMRVbwvjxzTiwytKJos4u/6TQp2.png';

// Black wordmark on a white field — render with the `invert` filter on dark surfaces.
export const LOGO_IMAGE =
  'https://cdn.magicpatterns.com/uploads/n7i2BD4MeCcXS1KtAmoa4D/ChatGPT_Image_Apr_28,_2026,_09_03_54_AM.png';
